import {
  ConfidencePill,
  ConflictAlertSeverityBadge,
  ConflictAlertStatusBadge,
  ConflictAlertTypeBadge,
} from '../../components';

import type { ConflictAlertVM, SupervisorVariant } from '../../view-models';
import type React from 'react';

// Placeholder honesto (Item 323): o perfil de User ainda não tem tela de destino —
// botão presente mas inerte. "Ver Pessoa" (323) e "Ver audit completo" (335) já ligam.
// Títulos honest-defer distintos do "Ver Pessoa" quando não navegável (Item 323):
// sem a cap de leitura, ou identificador redatado (LGPD), ou party que é User.
const PERSON_NO_CAP_TITLE = 'Disponível para quem pode abrir fichas de Pessoas';
const PERSON_REDACTED_TITLE =
  'Identificador redatado — disponível apenas com leitura sensível de auditoria';
const USER_PROFILE_SOON_TITLE = 'Perfil de usuário — em breve';
// Item 335 — honest-defer do "Ver audit completo" quando o caller não tem a cap.
const AUDIT_NO_CAP_TITLE = 'Disponível para quem pode abrir o Explorador de auditoria';

export interface CentroAlertasDrawerProps {
  alert: ConflictAlertVM;
  variant: SupervisorVariant;
  onClose: () => void;
  /**
   * Modo leitura (T7-4 — drawer do Histórico de alertas terminais). Quando true,
   * o rodapé de ações de lifecycle não renderiza (o alerta já é resolvido/revogado;
   * a reabertura usa o "Re-abrir" da própria linha do Histórico). Default false.
   */
  readOnly?: boolean;
  // Ações de lifecycle — opcionais p/ o modo leitura (não passadas no Histórico).
  onMitigate?: () => void;
  onAccept?: () => void;
  onFalsePositive?: () => void;
  /** Gated pela cap `conflict-alert-resolve-or-revoke` (Regulated opt-in). Ausente → botão Resolver não renderiza. */
  onResolve?: (() => void) | undefined;
  /**
   * Item 323 — navega p/ a ficha completa da Pessoa (`/pessoas/:id`) a partir de uma
   * party `kind: 'person'`. A `CentroAlertasPage` passa este callback (via `useNavigate`)
   * SÓ quando o caller tem `person-read` — espelha o gating por presença do `onResolve`.
   * Ausente → "Ver Pessoa" fica honest-defer (disabled + title de permissão). Party
   * redatada (`partiesRedacted`) também não navega, mesmo com o callback presente.
   */
  onOpenPerson?: ((personId: string) => void) | undefined;
  /**
   * Item 335 — navega p/ o Explorador de auditoria (`/admin/audit`) filtrado por este
   * ConflictAlert (`entityType=conflict_alert&entityId=<alert.id>`). A `CentroAlertasPage`
   * passa este callback (via `useNavigate`) SÓ quando o caller tem `audit-events-read` —
   * espelha o gating por presença do `onOpenPerson`/`onResolve`. Ausente → "Ver audit
   * completo" fica honest-defer (disabled + title de permissão). A seção inteira só
   * renderiza em `variant === 'regulated'` (comportamento preservado).
   */
  onOpenAudit?: ((alertId: string) => void) | undefined;
}

// Verbatim de design-references/brief-3/centro-alertas.app.jsx:439-569
// (DrawerInlineHeader + DrawerInlineBody combinados). window.X → imports;
// botões de navegação cross-tela = placeholder honesto (Item 323).
export function CentroAlertasDrawer({
  alert,
  variant,
  onClose,
  readOnly = false,
  onMitigate,
  onAccept,
  onFalsePositive,
  onResolve,
  onOpenPerson,
  onOpenAudit,
}: CentroAlertasDrawerProps): React.JSX.Element {
  const showOpId = variant === 'regulated';
  return (
    <>
      <div className="drawer__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ConflictAlertSeverityBadge severity={alert.severity} />
          <span className="drawer__title">Alerta de conflito</span>
        </div>
        <button className="btn btn--ghost btn--sm btn--icon" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>

      <div className="drawer__body">
        <section className="att-drawer__section">
          <div
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              marginBottom: 8,
              flexWrap: 'wrap',
            }}
          >
            <ConflictAlertTypeBadge alertType={alert.alertType} />
            <ConflictAlertStatusBadge status={alert.status} />
          </div>
          <div
            style={{
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--fg-1)',
              marginBottom: 4,
            }}
          >
            {alert.title}
          </div>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-2)', lineHeight: 1.5 }}>
            {alert.description}
          </div>
        </section>

        <section className="att-drawer__section">
          <div className="att-drawer__section-title">Partes envolvidas</div>
          {alert.partiesRedacted === true && (
            <div
              style={{
                fontSize: 'var(--text-2xs)',
                color: 'var(--fg-3)',
                marginBottom: 8,
              }}
            >
              🔒 Identificadores redatados — visíveis apenas com permissão de leitura sensível de
              auditoria.
            </div>
          )}
          {alert.parties.map((p, i) => (
            <div key={i} className="ca-drawer__party">
              <div className="ca-drawer__party-name">
                {p.name}{' '}
                <span style={{ color: 'var(--fg-3)', fontWeight: 'var(--fw-regular)' }}>
                  ({p.role})
                </span>
              </div>
              <div className="ca-drawer__party-meta">
                {p.kind === 'person' ? 'Pessoa' : 'Usuário'}
              </div>
              <div className="ca-drawer__party-attrs">{p.meta}</div>
              {p.kind === 'person' ? (
                // "Ver Pessoa →" → ficha /pessoas/:id. Habilitado só com o callback
                // (caller tem person-read) E party não redatada (id mascarado quebraria
                // a rota). Senão honest-defer com title específico do motivo.
                <button
                  className="btn-ghost-mini"
                  style={{ marginTop: 6 }}
                  disabled={onOpenPerson === undefined || alert.partiesRedacted === true}
                  title={
                    onOpenPerson === undefined
                      ? PERSON_NO_CAP_TITLE
                      : alert.partiesRedacted === true
                        ? PERSON_REDACTED_TITLE
                        : undefined
                  }
                  onClick={() => onOpenPerson?.(p.id)}
                >
                  Ver Pessoa →
                </button>
              ) : (
                // Party User: não há ficha de User — segue deferido (fora de escopo Item 323).
                <button
                  className="btn-ghost-mini"
                  style={{ marginTop: 6 }}
                  disabled
                  title={USER_PROFILE_SOON_TITLE}
                >
                  Ver perfil do usuário →
                </button>
              )}
            </div>
          ))}
        </section>

        <section className="att-drawer__section">
          <div className="att-drawer__section-title">Contexto de detecção</div>
          <div className="att-drawer__row">
            <span>Agente</span>
            <b className="mono" style={{ fontSize: 'var(--text-xs)' }}>
              {alert.agent}
            </b>
          </div>
          <div className="att-drawer__row">
            <span>Confiança</span>
            <ConfidencePill value={alert.confidence} />
          </div>
          <div className="att-drawer__row">
            <span>Gatilho</span>
            <b className="mono" style={{ fontSize: 'var(--text-2xs)' }}>
              {alert.trigger}
            </b>
          </div>
          <div className="att-drawer__row">
            <span>Detectado</span>
            <b className="mono">
              {alert.detectedAt} · há {alert.detectedAgo}
            </b>
          </div>
        </section>

        <section className="att-drawer__section">
          <div className="att-drawer__section-title">
            Reincidência · {alert.chain.position}/{alert.chain.total}
            {alert.chain.position >= 3 && (
              <span style={{ color: 'var(--danger-fg)', marginLeft: 8 }}>
                · reincidência crítica
              </span>
            )}
          </div>
          <ol className="ca-chain">
            {alert.chain.history.map((h) => {
              const isCurrent = h.id === alert.id;
              return (
                <li
                  key={h.id}
                  className={'ca-chain__step' + (isCurrent ? ' ca-chain__step--current' : '')}
                >
                  <div className="ca-chain__dot" aria-hidden="true" />
                  <div className="ca-chain__body">
                    <div className="ca-chain__head">
                      <span className="mono">{h.at}</span>
                      <ConflictAlertSeverityBadge severity={h.severity} />
                      <ConflictAlertStatusBadge status={h.status} />
                      {isCurrent && <span className="ca-chain__current">atual</span>}
                    </div>
                    <div className="ca-chain__note">{h.note}</div>
                  </div>
                </li>
              );
            })}
          </ol>
          {alert.chain.position >= 3 && (
            <div className="ca-chain__warn">
              ⚠ Padrão de reincidência sem mudança de contexto = sinal forte.
            </div>
          )}
        </section>

        {(alert.impact.pausedConversationIds.length > 0 ||
          alert.impact.blockedMcpToolCalls.length > 0) && (
          <section className="att-drawer__section att-drawer__section--warn">
            <div className="att-drawer__section-title">Cascata ativa</div>
            {alert.impact.pausedConversationIds.length > 0 && (
              <div style={{ fontSize: 'var(--text-sm)', marginBottom: 8 }}>
                <b>Conversas pausadas:</b> {alert.impact.pausedConversationIds.length}
              </div>
            )}
            {alert.impact.blockedMcpToolCalls.length > 0 && (
              <>
                <div style={{ fontSize: 'var(--text-sm)', marginBottom: 4 }}>
                  <b>Chamadas de ferramentas (MCP) bloqueadas:</b>
                </div>
                <ul className="ca-drawer__mcp-list">
                  {alert.impact.blockedMcpToolCalls.map((c, i) => (
                    <li key={i}>
                      <span style={{ color: 'var(--fg-3)' }}>{c.at}</span> —{' '}
                      <span style={{ color: 'var(--fg-1)' }}>{c.tool}</span>
                      <span
                        style={{
                          color: 'var(--danger-fg)',
                          marginLeft: 6,
                          fontWeight: 'var(--fw-semibold)',
                        }}
                      >
                        {c.status}
                      </span>
                    </li>
                  ))}
                </ul>
                {alert.impact.auditLogId != null && alert.impact.auditLogId !== '' && (
                  <div
                    style={{
                      fontSize: 'var(--text-2xs)',
                      color: 'var(--fg-3)',
                      marginTop: 6,
                    }}
                    className="mono"
                  >
                    audit log: {alert.impact.auditLogId}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {showOpId && (
          <section className="att-drawer__section">
            <div className="att-drawer__section-title">Trilha de auditoria · operationId</div>
            <div className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-2)' }}>
              {alert.operationId}
            </div>
            <button
              className="btn-ghost-mini"
              style={{ marginTop: 6 }}
              disabled={onOpenAudit === undefined}
              title={onOpenAudit === undefined ? AUDIT_NO_CAP_TITLE : undefined}
              onClick={() => onOpenAudit?.(alert.id)}
            >
              Ver audit completo →
            </button>
          </section>
        )}
      </div>

      {!readOnly && (
        <div className="drawer__foot">
          <div className="drawer__foot-actions">
            <button className="btn-ghost-mini" onClick={onFalsePositive}>
              Falso positivo
            </button>
            {onResolve !== undefined && (
              <button className="btn-ghost-mini" onClick={onResolve}>
                Resolver
              </button>
            )}
            <button className="btn-ghost-mini" onClick={onAccept}>
              Aceitar c/ just.
            </button>
            <button className="btn-ghost-mini btn-ghost-mini--primary" onClick={onMitigate}>
              Mitigar
            </button>
          </div>
        </div>
      )}
    </>
  );
}
